import { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import clip from 'text-clipper'
import { useDispatch } from 'react-redux'
import { colors, Avatar, Button, Divider, Menu, MenuItem, Typography, Tooltip } from '@mui/material'
import { hasRoles } from 'acl'
import { ADMIN_PATH } from 'features/admin'
import { constants as fromAccount } from 'features/account'
import { useSelectedFleets, useSettingsFleetValues, actions as fromSettings } from 'features/settings'
import { getAppConfig } from 'app-config'
import FleetSelector from './fleet-selector'
import { FleetIcon } from 'frame/components'
import Box from '@mui/material/Box'
import { themeV5 } from 'theme/v5'

const AccountPopover = ({
  anchor,
  linker,
  open,
  user,
  userRoles,
  onClose,
  onLogout
}) => {

  const config = getAppConfig()
  const dispatch = useDispatch()
  const selectedFleets = useSelectedFleets()
  const options = useSettingsFleetValues()

  const [showSelectFleetModal, setShowSelectFleetModal] = useState(false)

  const MAX_FLEET = 27
  const maxSelectedFleets = selectedFleets.length > MAX_FLEET
  const croppedSelectedfleets = maxSelectedFleets ? selectedFleets.slice(0, MAX_FLEET) : selectedFleets

  const [seeMore, setSeeMore] = useState(false)

  const onToggleFleetSelector = () => {
    dispatch(fromSettings.resetFlags()) // clear settings flags
    onClose()
    setShowSelectFleetModal(!showSelectFleetModal)
  }

  const hasFleets = !! options.length

  if ( ! anchor.current) {
    return null
  }

  const displayFleets = (fleets) => fleets.map((node, index) =>
    <FleetIcon key={`fleet-icon-${index}`} fleetName={node.label} />
  )

  return (
    <>
      <Menu
        keepMounted
        id="menu-account"
        anchorEl={anchor.current}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={onClose}>
        <Box
          sx={{
            width: 360,
            maxWidth: '100%',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'spaceBetween',
              alignItems: 'center',
              paddingY: 3,
              paddingX: 3,
              minWidth: 260,
            }}>
            <Avatar
              sx={{
                width: 46,
                height: 46,
              }}
              alt={user.nickname}
              src={user.picture}
              to="/"/>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                paddingLeft: '10px',
              }}>
              <Typography
                sx={{
                  maxWidth: '270px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'wrap',
                }}
                variant="h4Heading">
                {user.nickname}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  letterSpacing: '-0.04px',
                  lineHeight: '18px',
                  color: themeV5.palette.graph.secondary,
                }}>{user.email}</Typography>
            </Box>

          </Box>

          <Divider />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              paddingTop: '16px',
              paddingBottom: '16px',
              paddingLeft: '24px',
              paddingRight: '24px',
              width: '100%',
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}>
              <Typography
                sx={{
                  '& > span': {
                    color: themeV5.palette.graph.secondary,
                    fontSize: '12px'
                  }
                }}
                variant="h5">
                Selected Fleets <span>({selectedFleets.length})</span>
              </Typography>

              <Button
                sx={{
                  padding: '3.2px 12px',
                  fontSize: 12,
                  color: themeV5.palette.dark,
                  background: colors.grey[100],
                  transform: `translateY(1px)`,
                  textTransform: 'uppercase',
                }}
                data-cy="change" onClick={onToggleFleetSelector}>
                Change
              </Button>
            </Box>

            <Box
              sx={{
                padding: '8px 0px 8px 0px'
              }}>
              {hasFleets ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '320px',
                    flexWrap: 'wrap',
                    '& > *': {
                      marginTop: 1,
                      marginRight: 1,
                    },
                  }}>

                  {displayFleets(seeMore ? selectedFleets : croppedSelectedfleets)}

                  {selectedFleets.length === 1 && (
                    <Typography
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        marginLeft: 10,
                        minHeight: 26,
                      }}
                      variant="body2">
                      {clip(selectedFleets[0].label, 34)}
                      &nbsp;
                      {selectedFleets.length > 1 && (
                        <>
                          + {selectedFleets.length - 1} other{selectedFleets.length > 2 ? 's' : ''}
                        </>
                      )}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: themeV5.palette.error.main,
                  }}
                  variant="body1">
                  There are no fleets associated with this account. Please&nbsp;
                  <a
                    style={{
                      color: themeV5.palette.error.main,
                    }}
                    href={`mailto:${config.email.help}`}>
                    contact support
                  </a>
                  .
                </Typography>
              )}
            </Box>

            {maxSelectedFleets && (
              <Typography
                sx={{
                  fontSize: '12px',
                  letterSpacing: '-0.04px',
                  lineHeight: '18px'
                }}>
                <Tooltip title={!seeMore ? `${selectedFleets.length - MAX_FLEET} more` : 'hide'}>
                  <span
                    style={{
                      color: themeV5.palette.text.link,
                      textDecoration: 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setSeeMore(!seeMore)}>
                    {seeMore ? 'Hide' : 'See More'}
                  </span>
                </Tooltip>
              </Typography>
            )}

          </Box>
          <Divider />

          <Box
            sx={{
              padding: '8px 0px 16px 0px'
            }}>
            {/* {hasFleets && (
              <MenuItem onClick={onToggleFleetSelector}>
                <span className={classes.link}>Change fleet selection</span>
              </MenuItem>
            )} */}
            <MenuItem
              sx={{
                borderBottom: 'none !important',
                '@media(min-width: 600px)': {
                  minHeight: '48px',
                },
                fontSize: '14px'
              }}
              data-cy="my-account" onClick={linker(fromAccount.PATH_ALL)}>
              <span
                style={{
                  padding: '0px 0px 0px 8px',
                }}>My account</span>
            </MenuItem>

            {hasRoles(['admin', 'superadmin', 'fleetcontroller'], userRoles) && (
              <MenuItem
                sx={{
                  borderBottom: 'none !important',
                  '@media(min-width: 600px)': {
                    minHeight: '48px',
                  },
                  fontSize: '14px'
                }}
                data-cy="admin" onClick={linker(ADMIN_PATH)}>
                <span
                  style={{
                    padding: '0px 0px 0px 8px',
                  }}>Admin</span>
              </MenuItem>
            )}

            <MenuItem
              sx={{
                marginTop: 5,
                borderBottom: 'none !important',
                '@media(min-width: 600px)': {
                  minHeight: '48px',
                },
                fontSize: '14px'
              }}
              data-cy="logout"
              onClick={onLogout}>
              <span
                style={{
                  padding: '0px 0px 0px 8px',
                }}>Logout</span>
            </MenuItem>

          </Box>
        </Box>
      </Menu>

      {showSelectFleetModal && (
        <FleetSelector onClose={() => setShowSelectFleetModal(false)} />
      )}
    </>
  )
}

AccountPopover.propTypes = {
  anchor: PropTypes.object,
  linker: PropTypes.func.isRequired,
  open: PropTypes.bool,
  user: PropTypes.shape({
    email: PropTypes.string,
    nickname: PropTypes.string,
    picture: PropTypes.string,
  }).isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
}

const mapStateToProps = ({ settings }) => ({
  userRoles: settings.roles,
})

export default connect(mapStateToProps)(AccountPopover)
